import {Color} from '@benchling/nucleus-ui';
import {css, StyleSheet} from 'aphrodite';
import React, {useState} from 'react';

import {reviewButtonStyle, REVIEW_BUTTON_STYLE_FOR_STATUS} from 'notebook/reviews/ReviewActionButton';
import {ReviewBadgeStatus} from 'notebook/reviews/ReviewConstants';
import {NO_USER, TaskData, Worksheet, WorksheetTaskStatusType} from 'notebook/worksheets/WorksheetConstants';
import {WorksheetContext} from 'notebook/worksheets/WorksheetContext';
import {usePMEIsAndMethodTasks} from 'procedures/worksheets/ProcedureWorksheetContext';
import TransitionRunModal, {TransitionStatusType} from 'procedures/worksheets/TransitionRunModal';
import EntityStore from 'stores/EntityStore';
import {FONT_WEIGHT_PROXIMA_REGULAR, FONT_WEIGHT_PROXIMA_SEMIBOLD} from 'styles/FontConstants';
import Avatar, {HasAvatar} from 'ui-library/avatar/Avatar';
import {AvatarSize} from 'ui-library/avatar/AvatarConstants';
import {AsyncButtonLoadingBehavior, ControlledAsyncButton} from 'ui-library/buttons/AsyncButton';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {GRAY_DARKER, GRAY_SEMIDARK, NEAR_BLACK} from 'ui-library/Colors';
import Dropdown from 'ui-library/dropdowns/Dropdown';
import {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import legacyRunModal from 'ui-library/legacyRunModal';
import Menu from 'ui-library/menus/Menu';
import {MenuWidth} from 'ui-library/menus/MenuConstants';
import Option from 'ui-library/menus/Option';

const START_RUN_TEXT = 'Start run';
const COMPLETE_RUN_TEXT = 'Complete run';
const FAIL_RUN_TEXT = 'Fail run';
const SKIP_RUN_TEXT = 'Skip run';

// worksheet-supported statuses to override the ReviewBadge
export const worksheetStatusStyles = StyleSheet.create({
  skipped: {
    backgroundColor: Color.PURPLE_500,
    ':hover:not(.is-disabled)': {backgroundColor: Color.PURPLE_450},
    ':active:not(.is-disabled)': {backgroundColor: Color.PURPLE_400},
  },
  pending: {
    backgroundColor: Color.GRAY_600,
    ':hover:not(.is-disabled)': {backgroundColor: Color.GRAY_550},
    ':active:not(.is-disabled)': {backgroundColor: Color.GRAY_500},
  },
  gray: {backgroundColor: Color.GRAY_400},
});

const TASK_STATUS_TO_BUTTON_PROPS: Record<
  WorksheetTaskStatusType,
  {
    text: string;
    className: string;
    isDropdown?: boolean;
    icon?: BenchlingIconName;
    useLoadingSpinner?: boolean;
  }
> = {
  [WorksheetTaskStatusType.PENDING]: {
    text: 'Pending',
    className: css(reviewButtonStyle.button, worksheetStatusStyles.pending),
    isDropdown: true,
    icon: 'request-pending',
  },
  [WorksheetTaskStatusType.STARTING]: {
    text: 'Starting...',
    className: css(
      reviewButtonStyle.button,
      REVIEW_BUTTON_STYLE_FOR_STATUS[ReviewBadgeStatus.IN_PROGRESS],
      reviewButtonStyle.loadingButton
    ),
    isDropdown: false,
    useLoadingSpinner: true,
  },
  [WorksheetTaskStatusType.IN_PROGRESS]: {
    text: 'In Progress',
    className: css(reviewButtonStyle.button, REVIEW_BUTTON_STYLE_FOR_STATUS[ReviewBadgeStatus.IN_PROGRESS]),
    isDropdown: true,
    icon: 'timer',
  },
  [WorksheetTaskStatusType.COMPLETING]: {
    text: 'Completing...',
    className: css(
      reviewButtonStyle.button,
      REVIEW_BUTTON_STYLE_FOR_STATUS[ReviewBadgeStatus.ACCEPTED],
      reviewButtonStyle.loadingButton
    ),
    isDropdown: false,
    useLoadingSpinner: true,
  },
  [WorksheetTaskStatusType.COMPLETED]: {
    text: 'Completed',
    className: css(reviewButtonStyle.button, REVIEW_BUTTON_STYLE_FOR_STATUS[ReviewBadgeStatus.ACCEPTED]),
    isDropdown: true,
    icon: 'done',
  },
  [WorksheetTaskStatusType.FAILING]: {
    text: 'Failing...',
    className: css(
      reviewButtonStyle.button,
      REVIEW_BUTTON_STYLE_FOR_STATUS[ReviewBadgeStatus.REJECTED],
      reviewButtonStyle.loadingButton
    ),
    isDropdown: false,
    useLoadingSpinner: true,
  },
  [WorksheetTaskStatusType.FAILED]: {
    text: 'Failed',
    className: css(reviewButtonStyle.button, REVIEW_BUTTON_STYLE_FOR_STATUS[ReviewBadgeStatus.REJECTED]),
    isDropdown: true,
    icon: 'status-failed',
  },
  [WorksheetTaskStatusType.SKIPPING]: {
    text: 'Skipping...',
    className: css(reviewButtonStyle.button, worksheetStatusStyles.skipped, reviewButtonStyle.loadingButton),
    isDropdown: false,
    useLoadingSpinner: true,
  },
  [WorksheetTaskStatusType.SKIPPED]: {
    text: 'Skipped',
    className: css(reviewButtonStyle.button, worksheetStatusStyles.skipped),
    isDropdown: true,
    icon: 'forward-step',
  },
  // this state is used for Worksheets whose previous unit operation have not been completed
  [WorksheetTaskStatusType.NOT_READY]: {
    text: 'Pending',
    className: css(reviewButtonStyle.button, worksheetStatusStyles.gray),
    icon: 'request-pending',
  },
  // Note: We will not have the following statuses for Method tasks, but this keeps TypeScript happy since all
  // possible keys for WorksheetTaskStatusType are fulfilled
  [WorksheetTaskStatusType.READY]: {text: 'Pending', className: css(reviewButtonStyle.button)},
  [WorksheetTaskStatusType.AMENDING]: {
    text: 'Amending...',
    className: css(reviewButtonStyle.button, worksheetStatusStyles.gray),
    isDropdown: false,
    useLoadingSpinner: true,
  },
};

function getStatusSummaryDropdown(
  lastEditedUser: HasAvatar,
  status?: WorksheetTaskStatusType,
  timestamp?: string
): Array<JSX.Element> | undefined {
  // no dropdown menu options for not ready, ready status types as those are not supported.
  if (!status || status === WorksheetTaskStatusType.NOT_READY || status === WorksheetTaskStatusType.READY) {
    return undefined;
  }

  return [
    <Option contentClassName={css(styles.dropdownOptionContent)} isHovered={false} key="avatar-info">
      <div className={css(styles.dropdownDetails)}>
        <div className={css(styles.avatar)}>
          <Avatar entity={lastEditedUser} size={AvatarSize.SMALL} />
        </div>
        <div>
          <span className={css(styles.statusText)}>{TASK_STATUS_TO_BUTTON_PROPS[status].text}</span>
          <br />
          <span className={css(styles.timestampText)}>{timestamp}</span>
        </div>
      </div>
    </Option>,
  ];
}

const statusMappings: Record<string, {text: string; status: TransitionStatusType}> = {
  start: {text: START_RUN_TEXT, status: WorksheetTaskStatusType.IN_PROGRESS},
  complete: {text: COMPLETE_RUN_TEXT, status: WorksheetTaskStatusType.COMPLETED},
  fail: {text: FAIL_RUN_TEXT, status: WorksheetTaskStatusType.FAILED},
  skip: {text: SKIP_RUN_TEXT, status: WorksheetTaskStatusType.SKIPPED},
};

function useRunOption(props: {
  key: string;
  name: string;
  worksheetTaskData: TaskData;
  worksheet: Worksheet | null;
  refetch: () => Promise<void>;
  disableCompleteRunButton: boolean | undefined;
  // This needs to return a function that returns an Option element since Menu expections Option elements. We
  // can't have another functional component wrap this Option element since it will break the Menu component.
  // Additionally, it needs to be a useX function since we are using React hooks within it.
}): () => JSX.Element {
  const {key, name, worksheetTaskData, worksheet, refetch, disableCompleteRunButton} = props;
  const {text, status} = statusMappings[key];
  const pmeisAndTasks = usePMEIsAndMethodTasks();
  return () => (
    <Option
      key={`${key}-run`}
      dataTestAttrs={{component: 'WorksheetStatusDropdown', element: `${key}RunOption`}}
      onSelect={() =>
        legacyRunModal(TransitionRunModal, {
          name,
          currentUser: EntityStore.currentUser(),
          transitionToStatus: status,
          worksheetTaskStatus: worksheetTaskData.status,
          worksheet,
          onTaskUpdate: refetch,
          disableCompleteRunButton,
          pmeisAndTasks,
        })
      }
    >
      <span className={css(styles.optionText)}>{text}</span>
    </Option>
  );
}

function useDropdownOptions(
  status: WorksheetTaskStatusType | undefined,
  name: string,
  worksheetTaskData: TaskData,
  worksheet: Worksheet | null,
  refetch: () => Promise<void>,
  disableCompleteRunButton: boolean | undefined,
  lastEditedUser: HasAvatar,
  timestamp?: string
): Array<JSX.Element> | undefined {
  // Call hooks first, unconditionally
  const startOption = useRunOption({
    key: 'start',
    name,
    worksheetTaskData,
    worksheet,
    refetch,
    disableCompleteRunButton,
  });
  const completeOption = useRunOption({
    key: 'complete',
    name,
    worksheetTaskData,
    worksheet,
    refetch,
    disableCompleteRunButton,
  });
  const failOption = useRunOption({
    key: 'fail',
    name,
    worksheetTaskData,
    worksheet,
    refetch,
    disableCompleteRunButton,
  });
  const skipOption = useRunOption({
    key: 'skip',
    name,
    worksheetTaskData,
    worksheet,
    refetch,
    disableCompleteRunButton,
  });

  if (!status || status === WorksheetTaskStatusType.NOT_READY || status === WorksheetTaskStatusType.READY) {
    return undefined;
  }

  switch (status) {
    case WorksheetTaskStatusType.PENDING:
      return [startOption, failOption, skipOption].map((fn) => fn());
    case WorksheetTaskStatusType.IN_PROGRESS:
      return [completeOption, failOption, skipOption].map((fn) => fn());
    default:
      return getStatusSummaryDropdown(lastEditedUser, status, timestamp);
  }
}

interface WorksheetStatusButtonProps {
  disabled?: boolean;
  name: string;
  isDropdown?: boolean;
}

export default function WorksheetStatusButton({
  disabled,
  name,
  isDropdown,
}: WorksheetStatusButtonProps): JSX.Element {
  const {worksheetTaskData, worksheet, refetch, disableTransitionRunModalCompleteRunButton} =
    WorksheetContext.useContext();
  const {status, lastEditedUser, timestamp} = worksheetTaskData;

  const dropdownOptions = useDropdownOptions(
    status,
    name,
    worksheetTaskData,
    worksheet,
    refetch,
    disableTransitionRunModalCompleteRunButton,
    lastEditedUser ?? NO_USER,
    timestamp
  );
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const onToggleDropdown = (newDropdownState: boolean): void => {
    if (newDropdownState !== isDropdownOpen) {
      setIsDropdownOpen(newDropdownState);
    }
  };

  return (
    <Dropdown
      isOpen={isDropdownOpen}
      onToggle={onToggleDropdown}
      menu={
        <Menu width={MenuWidth.FIT_CONTENT} className={css(styles.dropdownRightAligned)}>
          {dropdownOptions}
        </Menu>
      }
    >
      {status &&
      TASK_STATUS_TO_BUTTON_PROPS[status] &&
      status &&
      TASK_STATUS_TO_BUTTON_PROPS[status].useLoadingSpinner ? (
        // A controlled async button is used here since the onClick function of the Button below is not only
        // async but it is also not limited to the duration of a singular request or task. Instead, we rely on
        // realtime messages to know when each of the individual tasks have transitioned. As such, we use a
        // ControlledAsyncButton to handle the loading state, when all of the tasks are not on the same status.
        <ControlledAsyncButton
          buttonStyle={ButtonStyle.PRIMARY}
          size={ButtonSize.MEDIUM}
          disabled={!(status && TASK_STATUS_TO_BUTTON_PROPS[status].isDropdown) || disabled}
          isDropdown={isDropdown}
          isLoading={true}
          onClick={async () => {}}
          loadingBehavior={AsyncButtonLoadingBehavior.SPINNER_WITH_TEXT}
          {...TASK_STATUS_TO_BUTTON_PROPS[status]}
        />
      ) : (
        <Button
          buttonStyle={ButtonStyle.PRIMARY}
          size={ButtonSize.MEDIUM}
          dataTestAttrs={{component: 'WorksheetStatusButton', element: 'button'}}
          disabled={!(status && TASK_STATUS_TO_BUTTON_PROPS[status].isDropdown) || disabled}
          isDropdown={isDropdown}
          {...(status && TASK_STATUS_TO_BUTTON_PROPS[status]
            ? TASK_STATUS_TO_BUTTON_PROPS[status]
            : TASK_STATUS_TO_BUTTON_PROPS[WorksheetTaskStatusType.NOT_READY])}
        />
      )}
    </Dropdown>
  );
}

const styles = StyleSheet.create({
  dropdownDetails: {
    display: 'flex',
    cursor: 'default',
  },
  dropdownRightAligned: {
    position: 'absolute',
    right: 0, // Aligns the dropdown with the right edge of the button
    top: '100%', // Places the dropdown right below the button
    minHeight: '36px', // match ReviewBadge height without tooltip overriding
  },
  dropdownOptionContent: {
    display: 'flex',
    padding: '0px',
    marginRight: '8px',
    marginLeft: '8px',
  },
  avatar: {
    marginRight: '8px',
  },
  statusText: {
    fontSize: '13px',
    fontWeight: FONT_WEIGHT_PROXIMA_SEMIBOLD,
    color: NEAR_BLACK,
    marginBottom: 4,
  },
  timestampText: {
    fontSize: '11px',
    fontWeight: FONT_WEIGHT_PROXIMA_REGULAR,
    lineHeight: '14px',
    color: GRAY_SEMIDARK,
  },
  // for dropdown options
  optionText: {
    fontSize: '13px',
    fontWeight: FONT_WEIGHT_PROXIMA_REGULAR,
    lineHeight: '16px',
    color: GRAY_DARKER,
  },
});
